<script setup lang="ts">
/**
 * An emailed invite's link, `/?invite=<token>` (server/teamSettings.ts). Stands
 * in for the whole app while the token is in the address: someone signed in
 * joins with one click; someone who is not makes an account here, or signs in
 * to the one they have. Either way it ends in a full reload onto the team's
 * board, so nothing started for the invite page lingers.
 */
import { computed, onMounted, ref } from "vue";
import { api } from "../lib/api.js";
import type { InviteInfo, User } from "@shared/types.js";

const props = defineProps<{ token: string; user: User | null }>();

const info = ref<InviteInfo | null>(null);
const gone = ref("");
const me = ref<User | null>(props.user);
const mode = ref<"new" | "signin">("new");
const error = ref("");
const busy = ref(false);

const displayName = ref("");
const username = ref("");
const password = ref("");
const again = ref("");

const mismatch = computed(() => mode.value === "new" && !!again.value && again.value !== password.value);

async function load(): Promise<void> {
  try {
    info.value = await api.get<InviteInfo>(`/api/invites/${encodeURIComponent(props.token)}`);
  } catch (err) {
    gone.value = err instanceof Error ? err.message : String(err);
  }
}
onMounted(load);

/** Onto the team's board, the invite out of the address. */
function goToBoard(teamId: string | undefined): void {
  location.replace(teamId ? `/?team=${encodeURIComponent(teamId)}` : "/");
}

async function accept(body: Record<string, string> = {}): Promise<void> {
  busy.value = true;
  error.value = "";
  try {
    const got = await api.post<{ teamId: string }>(`/api/invites/${encodeURIComponent(props.token)}/accept`, body);
    goToBoard(got.teamId);
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
    busy.value = false;
  }
}

async function submit(): Promise<void> {
  if (mode.value === "new") {
    if (mismatch.value) return;
    await accept({ displayName: displayName.value, username: username.value, password: password.value });
    return;
  }
  busy.value = true;
  error.value = "";
  try {
    me.value = (await api.post<{ user: User }>("/api/login", { username: username.value, password: password.value })).user;
    password.value = "";
    await load();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
    password.value = "";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="login invite">
    <form @submit.prevent="submit">
      <h1><img src="/favicon.svg" alt="" />todos</h1>

      <template v-if="gone">
        <p class="error">{{ gone }}</p>
        <button class="btn" type="button" @click="goToBoard(undefined)">Go to the board</button>
      </template>

      <p v-else-if="!info">…</p>

      <template v-else-if="me">
        <p v-if="info.member">You are already in <strong>{{ info.team }}</strong>.</p>
        <p v-else>{{ info.invitedBy }} has invited you to join <strong>{{ info.team }}</strong>.</p>
        <p class="invite-who">Signed in as {{ me.displayName }} (@{{ me.username }}).</p>
        <p class="error">{{ error }}</p>
        <button v-if="info.member" class="btn primary" type="button" @click="goToBoard(info.teamId)">Open the board</button>
        <button v-else class="btn primary" type="button" :disabled="busy" @click="accept()">{{ busy ? "…" : `Join ${info.team}` }}</button>
      </template>

      <template v-else>
        <p>{{ info.invitedBy }} has invited {{ info.email }} to join <strong>{{ info.team }}</strong>.</p>
        <div class="invite-modes" role="tablist">
          <button type="button" role="tab" class="btn ghost" :class="{ current: mode === 'new' }" :aria-selected="mode === 'new'" @click="mode = 'new'">
            New account
          </button>
          <button type="button" role="tab" class="btn ghost" :class="{ current: mode === 'signin' }" :aria-selected="mode === 'signin'" @click="mode = 'signin'">
            I have one
          </button>
        </div>
        <input v-if="mode === 'new'" v-model="displayName" placeholder="your name" autocomplete="name" />
        <input v-model="username" placeholder="username" autocomplete="username" autocapitalize="none" />
        <input
          v-model="password"
          type="password"
          placeholder="password"
          :autocomplete="mode === 'new' ? 'new-password' : 'current-password'"
          :minlength="mode === 'new' ? 8 : undefined"
        />
        <input v-if="mode === 'new'" v-model="again" type="password" placeholder="password again" autocomplete="new-password" :aria-invalid="mismatch" />
        <p class="error">{{ mismatch ? "The passwords differ" : error }}</p>
        <button class="btn primary" :disabled="busy || !username || !password || (mode === 'new' && (!again || mismatch))">
          {{ busy ? "…" : mode === "new" ? `Join ${info.team}` : "Sign in" }}
        </button>
      </template>
    </form>
  </div>
</template>

<style scoped>
.invite p {
  margin: 0;
  text-align: center;
}
.invite-who {
  opacity: 0.7;
  font-size: 0.9em;
}
.invite-modes {
  display: flex;
  gap: 6px;
  justify-content: center;
}
.invite-modes .current {
  font-weight: 600;
  text-decoration: underline;
}
</style>
